/**
 * Cart Delivery Time Component
 * 
 * A custom element that handles the scroll-based animation of delivery time information
 * in the cart drawer. As users scroll through cart items, this component smoothly
 * animates the delivery info upward while maintaining the sticky close button.
 */

class CartDeliveryTimeComponent extends HTMLElement {
  constructor() {
    super();
    
    // Private properties
    this._scrollHandler = null;
    this._mutationObserver = null;
    this._isInitialized = false;
  }

  connectedCallback() {
    if (this._isInitialized) return;
    
    this._initialize();
    this._isInitialized = true;
  }

  disconnectedCallback() {
    this._cleanup();
    this._isInitialized = false;
  }

  /**
   * Initialize the component
   */
  _initialize() {
    // Wait for the next tick to ensure DOM is ready
    requestAnimationFrame(() => {
      this._setupScrollDetection();
    });
  }

  /**
   * Set up scroll detection for the cart items area
   */
  _setupScrollDetection() {
    const cartItems = this._findCartItems();
    const deliveryInfo = this._findDeliveryInfo();
    
    if (cartItems && deliveryInfo) {
      this._scrollHandler = this._handleScroll.bind(this, deliveryInfo);
      cartItems.addEventListener('scroll', this._scrollHandler, { passive: true });
      
      // Set up mutation observer for dynamic content changes
      this._setupMutationObserver(cartItems);
    }
  }

  /**
   * Set up mutation observer to handle dynamic content changes
   * @param {HTMLElement} cartItems - The cart items container element
   */
  _setupMutationObserver(cartItems) {
    this._mutationObserver = new MutationObserver(() => {
      // Re-setup scroll detection if content changes
      this._cleanup();
      this._setupScrollDetection();
    });

    this._mutationObserver.observe(cartItems, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Handle scroll events to animate the delivery info
   * @param {HTMLElement} deliveryInfo - The delivery info element to animate
   */
  _handleScroll(deliveryInfo) {
    const cartItems = this._findCartItems();
    if (!cartItems || !deliveryInfo) return;

    const scrollTop = cartItems.scrollTop;
    const maxScroll = cartItems.scrollHeight - cartItems.clientHeight;
    
    // Calculate scroll percentage (0 to 1)
    const scrollPercentage = maxScroll > 0 ? Math.min(scrollTop / maxScroll, 1) : 0;
    
    // Apply scroll-based animations
    this._applyScrollAnimations(deliveryInfo, scrollTop, scrollPercentage);
  }

  /**
   * Apply scroll-based animations to the delivery info
   * @param {HTMLElement} deliveryInfo - The delivery info element to animate
   * @param {number} scrollTop - Current scroll position
   * @param {number} scrollPercentage - Scroll percentage (0 to 1)
   */
  _applyScrollAnimations(deliveryInfo, scrollTop, scrollPercentage) {
    // Move element up using margin-top to affect document flow
    const marginTop = -scrollTop * 0.3;
    deliveryInfo.style.marginTop = `${marginTop}px`;
    
    // Add subtle opacity effect
    const opacity = Math.max(0.8, 1 - (scrollPercentage * 0.2));
    deliveryInfo.style.opacity = String(opacity);
    
    // Add subtle scale effect
    const scale = Math.max(0.98, 1 - (scrollPercentage * 0.02));
    deliveryInfo.style.transform = `scale(${scale})`;
  }

  /**
   * Find the cart items container
   * @returns {HTMLElement|null}
   */
  _findCartItems() {
    const cartDrawer = this.closest('cart-drawer-component');
    return cartDrawer ? cartDrawer.querySelector('.cart-drawer__items') : null;
  }

  /**
   * Find the delivery info element
   * @returns {HTMLElement|null}
   */
  _findDeliveryInfo() {
    return this.querySelector('.postalcode-delivery-moment-information');
  }

  /**
   * Clean up event listeners and observers
   */
  _cleanup() {
    if (this._scrollHandler) {
      const cartItems = this._findCartItems();
      if (cartItems) {
        cartItems.removeEventListener('scroll', this._scrollHandler);
      }
      this._scrollHandler = null;
    }
    
    if (this._mutationObserver) {
      this._mutationObserver.disconnect();
      this._mutationObserver = null;
    }
  }

  /**
   * Public method to refresh the component
   */
  refresh() {
    this._cleanup();
    this._setupScrollDetection();
  }
}

// Register the custom element
if (!customElements.get('cart-delivery-time')) {
  customElements.define('cart-delivery-time', CartDeliveryTimeComponent);
}
